export interface Article {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  readTime: string;
  imageUrl: string;
  imageUrlCompact: string;
  content: ArticleContent[];
  tags: string[];
  author: string;
}

export interface ArticleContent {
  type: "heading" | "paragraph" | "list" | "tip" | "image";
  level?: number;
  text?: string;
  items?: string[];
}

export const articles: Article[] = [
  {
    id: "mai-origin-story",
    title: "不知火舞角色起源：从饿狼传说到拳皇的传奇之路",
    excerpt:
      "深入探究不知火舞的创作背景，从SNK经典格斗游戏《饿狼传说2》中首次登场，到成为《拳皇》系列当家花旦的完整历程，揭示这位女忍者背后的故事。",
    category: "character-background",
    date: "2026-07-15",
    readTime: "8 分钟",
    imageUrl: "https://readdy.ai/api/search-image?query=Japanese%20female%20ninja%20Mai%20Shiranui%20in%20traditional%20red%20and%20white%20costume%20holding%20a%20giant%20folding%20fan%20dynamic%20fighting%20pose%20against%20a%20traditional%20Japanese%20dojo%20background%20with%20cherry%20blossoms%20dramatic%20lighting%20anime%20art%20style%20highly%20detailed%20illustration&width=800&height=500&seq=mai-origin-feat&orientation=landscape",
    imageUrlCompact: "https://readdy.ai/api/search-image?query=Japanese%20female%20ninja%20Mai%20Shiranui%20in%20traditional%20red%20and%20white%20costume%20holding%20a%20giant%20folding%20fan%20dynamic%20fighting%20pose%20against%20a%20traditional%20Japanese%20dojo%20background%20with%20cherry%20blossoms%20dramatic%20lighting%20anime%20art%20style%20highly%20detailed%20illustration&width=400&height=260&seq=mai-origin-comp&orientation=landscape",
    content: [
      {
        type: "heading",
        level: 2,
        text: "诞生：SNK黄金时代的创作奇迹",
      },
      {
        type: "paragraph",
        text: "1992年，SNK在《饿狼传说2》中首次引入了不知火舞这一角色。彼时正值格斗游戏黄金年代，SNK希望创造一位兼具性感魅力与强大实力的女性格斗家。不知火舞的设计灵感融合了日本传统女忍者形象与现代审美，其标志性的红色忍者装束、巨型折扇以及「不知火流忍术」的战斗风格，迅速俘获了大量玩家的心。",
      },
      {
        type: "heading",
        level: 2,
        text: "角色设定：火焰中的舞者",
      },
      {
        type: "paragraph",
        text: "不知火舞是不知火流忍术的继承者，自幼跟随祖父不知火半藏修习忍术。她的战斗风格独具一格——将传统忍术与火焰之力完美融合，以华丽的舞姿配合致命攻击。她使用的标志性武器「花蝶扇」不仅是远程攻击的利器，更成为了格斗游戏史上最经典的武器设计之一。",
      },
      {
        type: "heading",
        level: 2,
        text: "与安迪·博加德的情感线",
      },
      {
        type: "paragraph",
        text: "不知火舞与安迪·博加德（Andy Bogard）的恋情是格斗游戏中最著名的CP之一。舞对安迪一见钟情，从此展开了热烈而执着的追求。然而安迪性格内敛，对感情表现迟钝，两人之间若即若离的关系成为了「饿狼传说」和「拳皇」系列中一道独特的风景线。这段情感线不仅丰富了角色层次，也让玩家在激烈的格斗之外感受到了温暖的人情味。",
      },
      {
        type: "heading",
        level: 2,
        text: "从配角到主角的蜕变",
      },
      {
        type: "paragraph",
        text: "随着《拳皇》系列的发展，不知火舞的戏份越来越重。从最初的客串角色，到与King、坂崎百合组成『女性格斗家队』，再到后来成为SNK全明星阵容中的核心角色。她的招式设计不断丰富，从最初的基础忍术攻击，到后来的「超必杀忍蜂」、「凤凰之舞」等华丽大招，每一次迭代都让玩家感受到SNK对这个角色的用心。",
      },
    ],
    tags: ["角色起源", "饿狼传说", "不知火流忍术", "安迪·博加德"],
    author: "拳皇档案馆",
  },
  {
    id: "mai-skill-complete",
    title: "不知火舞全技能深度解析：从必杀技到MAX超杀的完整图鉴",
    excerpt:
      "逐帧分析不知火舞的全部技能，包括普通技、特殊技、必杀技、超必杀技及MAX版本，附带帧数数据和实战应用场景，助你全面掌握舞的战斗体系。",
    category: "skill-system",
    date: "2026-07-20",
    readTime: "12 分钟",
    imageUrl: "https://readdy.ai/api/search-image?query=Mai%20Shiranui%20performing%20her%20signature%20ninja%20fire%20attack%20with%20dramatic%20flame%20effects%20dynamic%20action%20shot%20in%20Japanese%20fighting%20game%20art%20style%20intense%20orange%20and%20red%20flames%20against%20dark%20background%20highly%20detailed%20game%20illustration%20with%20motion%20blur%20effects&width=800&height=500&seq=mai-skills-feat&orientation=landscape",
    imageUrlCompact: "https://readdy.ai/api/search-image?query=Mai%20Shiranui%20performing%20her%20signature%20ninja%20fire%20attack%20with%20dramatic%20flame%20effects%20dynamic%20action%20shot%20in%20Japanese%20fighting%20game%20art%20style%20intense%20orange%20and%20red%20flames%20against%20dark%20background%20highly%20detailed%20game%20illustration%20with%20motion%20blur%20effects&width=400&height=260&seq=mai-skills-comp&orientation=landscape",
    content: [
      {
        type: "heading",
        level: 2,
        text: "普通技与特殊技",
      },
      {
        type: "paragraph",
        text: "不知火舞的普通技以快速和灵活著称。她的站立重拳、蹲下重脚都是优秀的确反和牵制工具。特殊技『龙炎舞』作为派生攻击，可以取消到多种必杀技中，是连招的重要衔接点。",
      },
      {
        type: "list",
        items: [
          "站重拳（5HP）：发生5帧，优秀的对空和确反技",
          "蹲重脚（2HK）：滑铲攻击，优秀的突进和下段择",
          "跳重脚（j.HK）：空中压制利器，判定范围广",
          "龙炎舞（6LK）：特殊技派生，可取消至必杀技",
          "三角跳（空中4/6）：墙壁反弹跳，增加空中变轨能力",
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "必杀技详解",
      },
      {
        type: "paragraph",
        text: "不知火舞的必杀技体系围绕「花蝶扇」展开。这把巨型折扇不仅是远程牵制工具，更是连招的核心组件。",
      },
      {
        type: "list",
        items: [
          "花蝶扇（236P）：投掷折扇，轻/重版本速度不同，是舞最重要的牵制技",
          "龙炎舞（623P）：升龙系对空技，无敌帧优秀，是对空的立回核心",
          "飞翔龙炎阵（214K）：空中突进技，可派生多种后续攻击",
          "必杀忍蜂（236K）：火焰突进技，命中后可取消至超必杀技",
          "小夜千鸟（214P）：近身投技，破防利器",
          "桃花鸟（41236K）：多段突进攻击，压制力极强",
        ],
      },
      {
        type: "tip",
        text: "【进阶技巧】花蝶扇的轻版本回收速度更快，适合近距离压制；重版本飞行距离更远，适合中远距离牵制。掌握两种版本的切换时机，是提升舞的上限的关键。",
      },
      {
        type: "heading",
        level: 2,
        text: "超必杀技与MAX超杀",
      },
      {
        type: "paragraph",
        text: "不知火舞的超必杀技以视觉冲击力强和伤害高著称，是翻盘的关键所在。",
      },
      {
        type: "list",
        items: [
          "超必杀忍蜂（236236K）：核心超杀，发生快、伤害高，可接在任何必杀技之后",
          "凤凰之舞（236236P）：全屏火焰攻击，优秀的版边压制超杀",
          "不知火流奥义·九尾（MAX）：MAX版本终极奥义，伤害突破天际，命中时演出效果震撼",
        ],
      },
    ],
    tags: ["技能解析", "必杀技", "超必杀技", "帧数数据", "实战技巧"],
    author: "拳皇研究室",
  },
  {
    id: "mai-combo-beginner",
    title: "新手上路：不知火舞基础连招从零教学",
    excerpt:
      "专为拳皇新手打造的不知火舞连招教程，从最基础的三连击到实用的BC连段，分步拆解，循序渐进，零基础也能快速上手。",
    category: "combo-guide",
    date: "2026-07-18",
    readTime: "10 分钟",
    imageUrl: "https://readdy.ai/api/search-image?query=Mai%20Shiranui%20in%20a%20training%20dojo%20setting%20practicing%20fighting%20stances%20with%20instructional%20overlay%20graphics%20showing%20combo%20inputs%20warm%20orange%20lighting%20Japanese%20wood%20interior%20anime%20illustration%20style%20clean%20and%20focused%20composition&width=800&height=500&seq=mai-combo-beginner-feat&orientation=landscape",
    imageUrlCompact: "https://readdy.ai/api/search-image?query=Mai%20Shiranui%20in%20a%20training%20dojo%20setting%20practicing%20fighting%20stances%20with%20instructional%20overlay%20graphics%20showing%20combo%20inputs%20warm%20orange%20lighting%20Japanese%20wood%20interior%20anime%20illustration%20style%20clean%20and%20focused%20composition&width=400&height=260&seq=mai-combo-beginner-comp&orientation=landscape",
    content: [
      {
        type: "heading",
        level: 2,
        text: "组合键入门：了解拳皇的按键系统",
      },
      {
        type: "paragraph",
        text: "在开始练习连招之前，首先需要了解拳皇的基本按键布局。拳皇使用四个攻击键：A（轻拳）、B（轻脚）、C（重拳）、D（重脚）。方向键使用数字小键盘表示法：236表示↓↘→，623表示→↓↘，214表示↓↙←。",
      },
      {
        type: "heading",
        level: 2,
        text: "基础三连击（Target Combo）",
      },
      {
        type: "paragraph",
        text: "不知火舞的基础三连击是最简单的连招入门，只需按顺序输入即可：",
      },
      {
        type: "list",
        items: [
          "近身站A → 站B → 站C：最简单的三连击，适合版边起手",
          "蹲A × 2 → 蹲B → 龙炎舞（623P）：下段起手的基础确认连段",
          "跳C → 站C → 花蝶扇（236P）：跳入起手的基础连段，稳定输出",
        ],
      },
      {
        type: "tip",
        text: "【练习建议】先在训练模式中将对手设置为「不防御」状态，反复练习每个连段的节奏感。重点感受每个招式之间的取消时机——这是连招成功的关键。",
      },
      {
        type: "heading",
        level: 2,
        text: "中阶连段：花蝶扇起手式",
      },
      {
        type: "paragraph",
        text: "掌握基础三连击后，可以开始学习以花蝶扇为核心的中阶连段：",
      },
      {
        type: "list",
        items: [
          "跳C → 站C → 花蝶扇(重) → 龙炎舞(轻)：中距离确认连段，推版能力强",
          "蹲B → 蹲A → 龙炎舞(轻) → 超必杀忍蜂：下段确认接超杀的经典连段",
          "站C → 龙炎舞(623P) → 飞翔龙炎阵(214K)：空中追击连段，适合版边",
        ],
      },
    ],
    tags: ["新手教程", "基础连招", "Target Combo", "入门指南"],
    author: "拳皇训练营",
  },
  {
    id: "mai-combo-advanced",
    title: "高手进阶：不知火舞BC连段与极限输出攻略",
    excerpt:
      "深度解析不知火舞的BC连段体系，包含多套高伤害连段配方、版边限定连和MAX模式下的极限输出方案，助你从天梯中脱颖而出。",
    category: "combo-guide",
    date: "2026-07-22",
    readTime: "15 分钟",
    imageUrl: "https://readdy.ai/api/search-image?query=Mai%20Shiranui%20unleashing%20a%20devastating%20MAX%20super%20move%20with%20intense%20red%20and%20orange%20fire%20aura%20dramatic%20close-up%20angle%20glowing%20embers%20floating%20in%20the%20air%20dark%20atmospheric%20background%20high-quality%20Japanese%20game%20illustration%20with%20epic%20scale%20and%20detail&width=800&height=500&seq=mai-combo-advanced-feat&orientation=landscape",
    imageUrlCompact: "https://readdy.ai/api/search-image?query=Mai%20Shiranui%20unleashing%20a%20devastating%20MAX%20super%20move%20with%20intense%20red%20and%20orange%20fire%20aura%20dramatic%20close-up%20angle%20glowing%20embers%20floating%20in%20the%20air%20dark%20atmospheric%20background%20high-quality%20Japanese%20game%20illustration%20with%20epic%20scale%20and%20detail&width=400&height=260&seq=mai-combo-advanced-comp&orientation=landscape",
    content: [
      {
        type: "heading",
        level: 2,
        text: "BC系统基础：什么是BC连段？",
      },
      {
        type: "paragraph",
        text: "BC连段（Blowback Cancel Combo）是拳皇系列中最具深度的连招系统。通过消耗一格超杀槽发动BC模式（按下B+C），进入「Quick MAX」状态，此时所有必杀技都可以互相取消，从而实现超长连段。掌握BC连段，是区分新手和高手的分水岭。",
      },
      {
        type: "heading",
        level: 2,
        text: "不知火舞BC核心配方",
      },
      {
        type: "list",
        items: [
          "站C → BC → 站C → 花蝶扇(轻) → 龙炎舞(轻) → 花蝶扇(轻) → 龙炎舞(轻) → 超必杀忍蜂：标准BC连段，伤害约480",
          "跳C → 站C → BC → 站C → 必杀忍蜂(轻) → 花蝶扇(轻) → 龙炎舞(重) → 凤凰之舞：版边限定BC，伤害约550",
          "蹲B → 蹲A → BC → 蹲C → 花蝶扇(轻) × 3 → 龙炎舞(重) → MAX超杀：下段确认BC，伤害约620",
        ],
      },
      {
        type: "tip",
        text: "【关键技巧】BC发动后的第一帧即可取消，所以站C → BC → 站C这套操作中，BC后的站C必须在BC发动的瞬间输入。练习时可以先放慢节奏，逐步提升速度。",
      },
    ],
    tags: ["BC连段", "高阶技巧", "MAX模式", "极限输出", "天梯指南"],
    author: "拳皇竞技场",
  },
  {
    id: "mai-cultural-legacy",
    title: "从游戏角色到文化符号：不知火舞的跨次元影响力",
    excerpt:
      "不知火舞如何从一个格斗游戏角色演变为全球流行文化符号？本文从游戏、动漫、Cosplay、电子竞技等多个维度，解析舞的文化穿透力。",
    category: "cultural-impact",
    date: "2026-07-10",
    readTime: "10 分钟",
    imageUrl: "https://readdy.ai/api/search-image?query=Mai%20Shiranui%20as%20a%20cultural%20icon%20artistic%20collage%20style%20showing%20her%20evolution%20across%20different%20media%20from%20classic%20game%20sprites%20to%20modern%203D%20renders%20vibrant%20red%20and%20gold%20tones%20creative%20montage%20composition%20celebration%20of%20Japanese%20game%20character%20design%20history&width=800&height=500&seq=mai-culture-feat&orientation=landscape",
    imageUrlCompact: "https://readdy.ai/api/search-image?query=Mai%20Shiranui%20as%20a%20cultural%20icon%20artistic%20collage%20style%20showing%20her%20evolution%20across%20different%20media%20from%20classic%20game%20sprites%20to%20modern%203D%20renders%20vibrant%20red%20and%20gold%20tones%20creative%20montage%20composition%20celebration%20of%20Japanese%20game%20character%20design%20history&width=400&height=260&seq=mai-culture-comp&orientation=landscape",
    content: [
      {
        type: "heading",
        level: 2,
        text: "游戏界的常青树",
      },
      {
        type: "paragraph",
        text: "自1992年首次登场以来，不知火舞已经活跃了超过三十年。她先后出现在《饿狼传说》系列、《拳皇》系列、《SNK VS. Capcom》、《死或生》、《王者荣耀》等众多游戏中，成为了游戏史上跨界出演次数最多的女性角色之一。这种跨作品的生命力，在格斗游戏角色中堪称独一无二。",
      },
      {
        type: "heading",
        level: 2,
        text: "Cosplay文化的不朽传奇",
      },
      {
        type: "paragraph",
        text: "不知火舞是全世界Cosplay爱好者最热衷于扮演的角色之一。她那标志性的红色忍者装束——以大胆的剪裁和鲜明的配色——既具挑战性又极具辨识度。从东京Comiket到圣迭戈漫展，从亚洲到欧美，不知火舞的Cosplay作品层出不穷。每一位Coser对这个角色的独特诠释，都在不断丰富她的文化内涵。",
      },
      {
        type: "heading",
        level: 2,
        text: "电子竞技中的女忍者",
      },
      {
        type: "paragraph",
        text: "在《拳皇》系列的国际赛事中，不知火舞始终是热门选择。日本传奇玩家「大御所」、中国顶尖选手「小孩」曾卓君等，都曾用舞在重要比赛中打出精彩表现。舞的上手难度适中、上限极高，是许多职业选手的副角色首选。",
      },
    ],
    tags: ["文化影响", "Cosplay", "电子竞技", "跨界合作", "流行文化"],
    author: "格斗文化志",
  },
  {
    id: "snk-female-ranking",
    title: "SNK女角色实力排行TOP10：谁是最强女格斗家？",
    excerpt:
      "综合剧情设定、游戏性能和人气投票三大维度，评选SNK宇宙中最强大的十位女性格斗家。不知火舞能排第几？一起来看看这份权威排行。",
    category: "character-ranking",
    date: "2026-07-25",
    readTime: "9 分钟",
    imageUrl: "https://readdy.ai/api/search-image?query=Collage%20of%20top%20SNK%20female%20fighters%20in%20dynamic%20action%20poses%20competitive%20ranking%20aesthetic%20with%20gold%20silver%20bronze%20accents%20dark%20elegant%20background%20with%20Japanese%20patterns%20premium%20game%20magazine%20cover%20style%20dramatic%20lighting%20highlighting%20each%20character&width=800&height=500&seq=snk-ranking-feat&orientation=landscape",
    imageUrlCompact: "https://readdy.ai/api/search-image?query=Collage%20of%20top%20SNK%20female%20fighters%20in%20dynamic%20action%20poses%20competitive%20ranking%20aesthetic%20with%20gold%20silver%20bronze%20accents%20dark%20elegant%20background%20with%20Japanese%20patterns%20premium%20game%20magazine%20cover%20style%20dramatic%20lighting%20highlighting%20each%20character&width=400&height=260&seq=snk-ranking-comp&orientation=landscape",
    content: [
      {
        type: "heading",
        level: 2,
        text: "评选标准说明",
      },
      {
        type: "paragraph",
        text: "本次排行综合以下三个维度进行评分：剧情实力（30%）——角色在官方设定中的战力水平；游戏性能（40%）——在当前版本中的竞技表现；人气投票（30%）——在全球玩家社区中的受欢迎程度。",
      },
      {
        type: "heading",
        level: 2,
        text: "TOP 10 完整榜单",
      },
      {
        type: "list",
        items: [
          "第10名：布鲁·玛丽（Blue Mary）—— 综合格斗技达人，投技角色天花板",
          "第9名：梁（Luong）—— 神秘优雅的长腿御姐，中距离王者",
          "第8名：温妮莎（Vanessa）—— 拳击手阿姨，近身压制力极强",
          "第7名：伊丽莎白（Elisabeth）—— 光之力继承者，剧情实力顶尖",
          "第6名：莉安娜（Leona）—— 大蛇之血暴走战士，爆发力恐怖",
          "第5名：King —— 泰拳女王，中距离压制与连段性能兼备",
          "第4名：不知火舞（Mai Shiranui）—— 本榜焦点！综合实力与人气双丰收",
          "第3名：雅典娜（Athena）—— 超能力偶像，多变的战斗风格",
          "第2名：库拉（Kula）—— 冰之少女，反K'级战力",
          "第1名：神乐千鹤（Chizuru）—— 三神器守护者，剧情与游戏双料冠军",
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "不知火舞排名分析",
      },
      {
        type: "paragraph",
        text: "不知火舞在此次排行中位列第四，主要得益于其超高的人气加分和稳定的游戏性能。在剧情方面，舞并非最强战力，但她的人气得分几乎满分——作为SNK最具辨识度的女性角色，舞在全球玩家心中的地位无可撼动。在游戏性能方面，舞属于「易上手难精通」的全能型角色，无论在哪个版本都能保持稳定的使用率和胜率。",
      },
    ],
    tags: ["角色排行", "SNK女角色", "TOP10", "实力分析", "人气投票"],
    author: "拳皇档案馆",
  },
  {
    id: "mai-kof15-guide",
    title: "拳皇15不知火舞完全攻略：版本最强配装与对战策略",
    excerpt:
      "针对拳皇15最新版本的不知火舞深度攻略，包含最佳连段更新、对阵各角色的优劣势分析以及排位上分策略，助你在KOF XV中称霸天梯。",
    category: "combo-guide",
    date: "2026-08-01",
    readTime: "14 分钟",
    imageUrl: "https://readdy.ai/api/search-image?query=Mai%20Shiranui%20in%20King%20of%20Fighters%20XV%20style%20render%20dramatic%20battle%20arena%20background%20with%20neon%20lights%20and%20crowd%20modern%203D%20game%20art%20style%20vibrant%20red%20costume%20glowing%20with%20fire%20energy%20competitive%20gaming%20atmosphere%20high%20resolution%20game%20artwork&width=800&height=500&seq=mai-kof15-feat&orientation=landscape",
    imageUrlCompact: "https://readdy.ai/api/search-image?query=Mai%20Shiranui%20in%20King%20of%20Fighters%20XV%20style%20render%20dramatic%20battle%20arena%20background%20with%20neon%20lights%20and%20crowd%20modern%203D%20game%20art%20style%20vibrant%20red%20costume%20glowing%20with%20fire%20energy%20competitive%20gaming%20atmosphere%20high%20resolution%20game%20artwork&width=400&height=260&seq=mai-kof15-comp&orientation=landscape",
    content: [
      {
        type: "heading",
        level: 2,
        text: "KOF XV版本舞的变化",
      },
      {
        type: "paragraph",
        text: "拳皇15中的不知火舞相比前作有了显著的性能提升。新增的「不知火流奥义·九尾」极大增强了她的翻盘能力，配合改良后的BC系统，舞的连段伤害上限进一步突破。",
      },
      {
        type: "heading",
        level: 2,
        text: "推荐点位与配装",
      },
      {
        type: "list",
        items: [
          "先锋位：利用舞优秀的立回能力和低资源消耗，为后续角色积累气槽",
          "中坚位：舞的最佳位置，配合1-2格气可以打出完整的BC连段",
          "大将位：不推荐，但MAX模式下九尾的翻盘能力不容小觑",
        ],
      },
    ],
    tags: ["拳皇15", "版本攻略", "天梯上分", "角色配装"],
    author: "拳皇竞技场",
  },
  {
    id: "mai-technique-secrets",
    title: "不知火舞十大隐藏技巧：连职业玩家都在用的进阶秘笈",
    excerpt:
      "揭秘不知火舞那些不为人知的隐藏技巧和高端操作，从帧数陷阱设置到择的心理学博弈，了解职业选手不会公开告诉你的小秘密。",
    category: "skill-system",
    date: "2026-07-28",
    readTime: "11 分钟",
    imageUrl: "https://readdy.ai/api/search-image?query=Close-up%20action%20shot%20of%20Mai%20Shiranui%20in%20a%20mysterious%20ninja%20pose%20partially%20hidden%20by%20shadows%20looking%20directly%20at%20viewer%20with%20confident%20expression%20traditional%20Japanese%20paper%20doors%20in%20background%20cinematic%20lighting%20with%20warm%20amber%20tones%20high-end%20illustration%20quality&width=800&height=500&seq=mai-secrets-feat&orientation=landscape",
    imageUrlCompact: "https://readdy.ai/api/search-image?query=Close-up%20action%20shot%20of%20Mai%20Shiranui%20in%20a%20mysterious%20ninja%20pose%20partially%20hidden%20by%20shadows%20looking%20directly%20at%20viewer%20with%20confident%20expression%20traditional%20Japanese%20paper%20doors%20in%20background%20cinematic%20lighting%20with%20warm%20amber%20tones%20high-end%20illustration%20quality&width=400&height=260&seq=mai-secrets-comp&orientation=landscape",
    content: [
      {
        type: "heading",
        level: 2,
        text: "技巧一：花蝶扇的帧数陷阱",
      },
      {
        type: "paragraph",
        text: "重花蝶扇命中后舞有+3帧的有利状态。利用这个帧数优势，可以设置多种后续择：前冲投、站C确认、下段起手等。对手在防御花蝶扇后的第一反应往往是站防或跳，针对性地选择择法可以大幅提升压制成功率。",
      },
      {
        type: "heading",
        level: 2,
        text: "技巧二：隐身帧的妙用",
      },
      {
        type: "paragraph",
        text: "龙炎舞的上升阶段具有优秀的对空无敌帧。但鲜为人知的是，龙炎舞的下落阶段也有短暂的无敌帧。在特定距离下，可以利用这段无敌帧穿过对手的飞行道具进行反击。",
      },
    ],
    tags: ["隐藏技巧", "帧数陷阱", "进阶秘笈", "职业技巧"],
    author: "拳皇研究室",
  },
];

export const getArticlesByCategory = (categorySlug: string): Article[] => {
  return articles.filter((a) => a.category === categorySlug);
};

export const getArticleById = (id: string): Article | undefined => {
  return articles.find((a) => a.id === id);
};

export const getFeaturedArticles = (): Article[] => {
  const featuredIds = [
    "mai-origin-story",
    "mai-skill-complete",
    "mai-combo-advanced",
    "mai-cultural-legacy",
    "snk-female-ranking",
    "mai-kof15-guide",
  ];
  return featuredIds.map((id) => articles.find((a) => a.id === id)!).filter(Boolean);
};